import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { try_for_free } from "../../url";
import RefSiteFooter from "./RefSiteFooter";
import RefPricingTeaser from "./RefPricingTeaser";
import Map from "../aboutus/map";
import aboutus_main from "../../Assets/aboutus_main.png";
import aboutus_sec1 from "../../Assets/aboutus_sec1.png";
import aboutus_sec2 from "../../Assets/aboutus_sec2.png";

const ABOUT_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&display=swap');
  :root {
    --rsa-ink: #163A5A; --rsa-ink2: #3D6A8C; --rsa-ink3: #5E87A3;
    --rsa-sky: #1A63A5; --rsa-mint: #2AB6E8;
    --rsa-secondary: #2AB6E8;
    --rsa-secondary-deep: #1AA3D4;
    --rsa-secondary-soft: #D9F4FC;
    --rsa-secondary-muted: rgba(42,182,232,0.18);
    --rsa-sky-light: #E8F4FC;
    --rsa-sky-hover: #155a94;
    --rsa-bg-dark: #0E2D48;
    --rsa-white: #FFFFFF; --rsa-surface: #F5F9FC;
    --rsa-ff-head: 'Plus Jakarta Sans', system-ui, sans-serif; --rsa-ff-body: 'Plus Jakarta Sans', system-ui, sans-serif;
  }
  .rsa-page { font-family: var(--rsa-ff-body); color: var(--rsa-ink); background: var(--rsa-white); position: relative; overflow: hidden; }

  /* HERO */
  .rsa-hero {
    text-align: center; padding: 64px 24px 32px;
    background: linear-gradient(180deg, #E8F4FC 0%, var(--rsa-secondary-soft) 35%, #fff 100%);
    position: relative; overflow: hidden;
  }
  .rsa-hero::before {
    content: ''; position: absolute; top: -150px; left: 50%; transform: translateX(-50%);
    width: 900px; height: 500px;
    background: radial-gradient(ellipse, rgba(26,99,165,.08) 0%, transparent 65%);
    pointer-events: none;
  }
  .rsa-hero-orb {
    position: absolute; width: 400px; height: 400px; border-radius: 50%;
    background: radial-gradient(circle, rgba(42,182,232,0.15) 0%, transparent 60%);
    top: -100px; right: -100px; z-index: 0; filter: blur(20px);
  }
  .rsa-hero-content { position: relative; z-index: 1; max-width: 800px; margin: 0 auto; }
  .rsa-h1 { font-family: var(--rsa-ff-head); font-size: clamp(38px, 6vw, 60px); font-weight: 800; letter-spacing: -.02em; line-height: 1.08; margin-bottom: 16px; }
  .rsa-h1-grad { background: linear-gradient(135deg, var(--rsa-sky), var(--rsa-mint)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .rsa-hero-sub { font-size: 18px; color: var(--rsa-ink2); font-weight: 400; max-width: 620px; margin: 0 auto 16px; line-height: 1.6; }
  
  /* SECTIONS */
  .rsa-section { padding: 80px 24px; max-width: 1240px; margin: 0 auto; box-sizing: border-box; }
  .rsa-section-alt { background: var(--rsa-bg-dark); padding: 88px 32px; }
  .rsa-section-alt-inner { max-width: 1240px; margin: 0 auto; }

  /* Dark section overrides */
  .rsa-section-alt .rsa-h2 { color: #fff; }
  .rsa-section-alt .rsa-p { color: rgba(255,255,255,0.7); }
  .rsa-section-alt .rsa-section-label { background: rgba(42,182,232,.15); color: var(--rsa-mint); }

  .rsa-grid { display: grid; grid-template-columns: 1fr 1.15fr; gap: 64px; align-items: center; margin-bottom: 88px; }
  .rsa-grid:last-child { margin-bottom: 0; }
  .rsa-grid.reverse { grid-template-columns: 1.15fr 1fr; direction: rtl; }
  .rsa-grid.reverse > * { direction: ltr; }

  .rsa-text-block { position: relative; z-index: 2; }
  .rsa-section-label { display: inline-block; font-size: 11px; font-weight: 700; color: var(--rsa-sky); background: var(--rsa-sky-light); padding: 4px 10px; border-radius: 6px; letter-spacing: .08em; margin-bottom: 16px; text-transform: uppercase; }
  .rsa-h2 { font-family: var(--rsa-ff-head); font-size: clamp(30px, 4vw, 42px); font-weight: 800; line-height: 1.15; letter-spacing: -.02em; color: var(--rsa-ink); margin-bottom: 20px; }
  .rsa-p { font-size: 16.5px; color: var(--rsa-ink2); line-height: 1.7; margin-bottom: 18px; font-weight: 400; }
  
  .rsa-image-wrap { 
    position: relative; border-radius: 20px; z-index: 1; 
    transition: all 0.25s; border: 1px solid transparent;
  }
  .rsa-image-wrap:hover { 
    transform: translateY(-4px); 
    box-shadow: 0 16px 40px rgba(26,99,165,.1); 
    border-color: rgba(26,99,165,.28); 
  }
  
  .rsa-img { width: 100%; height: auto; display: block; border-radius: 20px; box-shadow: 0 24px 64px rgba(22,58,90,.12); }

  /* BUTTONS */
  .rsa-btn {
    font-family: var(--rsa-ff-body); font-size: 14.5px; font-weight: 600;
    padding: 13px 26px; border-radius: 10px; cursor: pointer;
    transition: all .2s; border: none; text-decoration: none; display: inline-flex; align-items: center;
    white-space: nowrap;
  }
  .rsa-btn-primary { background: linear-gradient(135deg, var(--rsa-sky) 0%, var(--rsa-secondary-deep) 100%); color: #fff; box-shadow: 0 4px 14px rgba(42,182,232,0.35); }
  .rsa-btn-primary:hover { background: linear-gradient(135deg, var(--rsa-sky-hover) 0%, var(--rsa-sky) 100%); transform: translateY(-2px); box-shadow: 0 6px 18px rgba(42,182,232,0.4); }
  .rsa-btn-link { background: transparent; color: var(--rsa-sky); font-size: 14px; font-weight: 600; padding: 12px 0; border: none; cursor: pointer; transition: color .2s; }
  .rsa-btn-link:hover { color: var(--rsa-sky-hover); }

  /* MAP SECTION */
  .rsa-map-section { background: var(--rsa-white); padding: 40px 0 60px; border-top: 1px solid #E8EAF2; }

  @media (max-width: 1024px) {
    .rsa-grid { gap: 40px; grid-template-columns: 1fr; }
    .rsa-grid.reverse { grid-template-columns: 1fr; }
  }
  @media (max-width: 600px) {
    .rsa-hero { padding: 48px 20px 32px; }
    .rsa-h1 { font-size: 34px; }
    .rsa-hero-sub { font-size: 15px; }
    .rsa-h2 { font-size: 28px; }
    .rsa-p { font-size: 15px; }
    .rsa-grid { gap: 32px; }
    .rsa-grid.reverse { direction: ltr; }
    .rsa-section, .rsa-section-alt { padding: 48px 20px; }
    .rsa-btn { width: 100%; justify-content: center; box-sizing: border-box; }
  }
`;

function RefAbout() {
  const { t } = useTranslation('about');
  const navigate = useNavigate();

  return (
    <div className="rsa-page">
      <style>{ABOUT_STYLES}</style>
      
      {/* HERO */}
      <div className="rsa-hero">
        <div className="rsa-hero-orb" />
        <div className="rsa-hero-content">
          <h1 className="rsa-h1">
            {t('hero.titlePrefix')}<span className="rsa-h1-grad">{t('hero.titleHighlight')}</span>
          </h1>
          <p className="rsa-hero-sub">
            {t('hero.subtitle')}
          </p>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="rsa-section">
        <div className="rsa-grid">
          <div className="rsa-text-block">
            <div className="rsa-section-label">{t('platform.label')}</div>
            <h2 className="rsa-h2">{t('platform.title')}</h2>
            <p
              className="rsa-p"
              style={{
                fontSize: "22px",
                fontWeight: 600,
                color: "var(--rsa-ink)",
              }}
            >
              {t('platform.subtitlePrefix')}
              <span style={{ color: "var(--rsa-sky)" }}>
                {t('platform.subtitleHighlight')}
              </span>
            </p>
            <p className="rsa-p">
              {t('platform.p1')}
            </p>
            <p className="rsa-p">{t('platform.p2')}</p>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                marginTop: "24px",
                flexWrap: "wrap"
              }}
            >
              <a
                href={try_for_free}
                target="_blank"
                rel="noreferrer"
                className="rsa-btn rsa-btn-primary"
              >
                {t('platform.tryFree')}
              </a>
              <button
                className="rsa-btn-link"
                onClick={() => navigate("/pricing")}
              >
                {t('platform.explorePricing')}
              </button>
            </div>
          </div>
          <div className="rsa-image-wrap">
            <img
              src={aboutus_main}
              alt="RequireSign Platform"
              className="rsa-img"
            />
          </div>
        </div>
      </div>

      {/* FEATURE SECTIONS */}
      <div className="rsa-section-alt">
        <div className="rsa-section-alt-inner">
          <div className="rsa-grid">
            <div className="rsa-image-wrap">
              <img src={aboutus_sec1} alt="Expert Team" className="rsa-img" />
            </div>
            <div className="rsa-text-block">
              <div className="rsa-section-label">{t('team.label')}</div>
              <h2 className="rsa-h2">{t('team.title')}</h2>
              <p className="rsa-p">
                {t('team.description')}
              </p>
            </div>
          </div>

          <div className="rsa-grid reverse">
            <div className="rsa-image-wrap">
              <img
                src={aboutus_sec2}
                alt="Global Solution"
                className="rsa-img"
              />
            </div>
            <div className="rsa-text-block">
              <div className="rsa-section-label">{t('solution.label')}</div>
              <h2 className="rsa-h2">{t('solution.title')}</h2>
              <p className="rsa-p">
                {t('solution.description')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* MAP */}
      <div className="rsa-map-section">
        <Map />
      </div>

      {/* PRICING TEASER */}
      <RefPricingTeaser />

      {/* FOOTER */}
      <RefSiteFooter />
    </div>
  );
}

export default RefAbout;
